import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useLocation from 'app/hooks/location';
import Loading from 'app/loading';
import { patchChildJournal } from 'child-journals/api';
import { getGenderOptions } from 'collections/edit/child-journal/options';
import { EDITIONS } from 'child-journals/editions';
import { groupJournalsByChild } from 'child-journals/group-by-child';

import { Page, ConfirmBar, BackButton, PrimaryButton, ErrorText } from '../styled';
import { DashboardTopNav } from '../chrome';
import Chevron from '../chevron';
import {
  revalidateDashboard,
  serverErrorMessage,
  useGiftCertificates,
  useChildJournal,
  useMyAllChildJournals,
  PLACEHOLDER_NAME,
} from '../hooks';
import {
  SetupContent,
  SetupHeading,
  SetupHint,
  FieldLabel,
  TextInput,
  DateSelect,
  DateRow,
  OptionPill,
  OptionList,
  ChoiceCard,
  ChoiceTitle,
  ChoiceCopy,
  TextLink,
} from './styled';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const CURRENT_YEAR = new Date().getFullYear();
// A couple of years out covers a due date; back to a generous age range covers
// a birthdate entered for an older child.
const YEARS = Array.from({ length: CURRENT_YEAR + 2 - 2015 }, (_, i) => CURRENT_YEAR + 1 - i);

// Relationship choices as shown in the design (the shared `relationshipOptions`
// list omits Aunt/Uncle, so this flow defines its own while keeping the same
// `subject-relation` attribute the rest of the app writes).
const RELATIONSHIPS = ['Mom', 'Dad', 'Grandmother', 'Grandfather', 'Aunt', 'Uncle', 'Other'];

const GENDERS = getGenderOptions('child-journals');

const STEP_WHO = 'who';
const STEP_RECIPIENT = 'recipient';
const STEP_CHILD = 'child';
const STEP_BABY = 'baby';
const STEP_EDITION = 'edition';
const STEP_RELATIONSHIP = 'relationship';
const STEPS = [STEP_WHO, STEP_RECIPIENT, STEP_CHILD, STEP_BABY, STEP_EDITION, STEP_RELATIONSHIP];

// The FMB ("Me") and GG ("Someone else") experiences for one book.
//
// The journal id comes from the URL. It was created — and its book entitlement
// assigned — when the customer pressed "Set up" on the dashboard, so this flow
// only ever UPDATES that journal, and the id survives a refresh mid-flow.
const AccountSetup = () => {
  // Two entry points: `/account/setup/:journalId` (set up an unassigned book) and
  // `/account/certificate/:giftCertificateId` (edit an existing certificate). In
  // the latter the certificate id is all the route carries — the journal and the
  // recipient are read back off the gift-certificates list.
  const { journalId: journalIdParam, giftCertificateId } = useParams();
  const { push, goBack, replace } = useHistory();
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);

  const isEditingCertificate = !!giftCertificateId;
  const { giftCertificates, isLoading: giftsLoading } = useGiftCertificates();
  const certificate = isEditingCertificate
    ? giftCertificates.find(gift => String(gift.id) === String(giftCertificateId))
    : null;

  const journalId = journalIdParam || certificate?.childJournalId;

  // The step lives in the URL, so each one is its own history entry: browser
  // Back (and the Back button, which uses it) walks the wizard in reverse, and a
  // refresh mid-flow stays on the same step instead of restarting.
  // `flow=redemption` enters this component from the gift-redemption flow. The
  // journal already exists (it transferred with the gift), so that flow needs only
  // the "About the baby" and relationship steps and then leaves for the journal —
  // it never sees the who-is-this-for or recipient steps.
  const isRedemption = params.get('flow') === 'redemption';
  const isGcFlow = params.get('for') === 'gift';

  const requestedStep = params.get('step');
  const entryStep = isRedemption
    ? STEP_BABY
    : (isEditingCertificate ? STEP_RECIPIENT : STEP_WHO);
  const step = STEPS.includes(requestedStep) ? requestedStep : entryStep;
  const isGift = !isRedemption && (isEditingCertificate || params.get('for') === 'gift');

  const goToStep = (nextStep, extraParams = {}) => {
    const next = new URLSearchParams(search);
    next.set('step', nextStep);
    Object.entries(extraParams).forEach(([key, value]) => next.set(key, value));
    push(`${pathname}?${next.toString()}`);
  };

  const [recipientName, setRecipientName] = useState('');
  const [babyName, setBabyName] = useState('');
  const [gender, setGender] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [edition, setEdition] = useState('first_year');
  const [relationship, setRelationship] = useState('');
  // Set when the member picks "an existing child" in STEP_CHILD rather than
  // "+ Add a new child" — carries that child's details forward so STEP_BABY and
  // STEP_RELATIONSHIP can be skipped for them (see onExistingChildSelect).
  const [existingChild, setExistingChild] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // The children this member already has a journal for, so "which child is this
  // book about?" can offer them instead of asking for the same child's details
  // again. The API has no child/parent relationship (see
  // docs/child_journal_editions.md §4) — grouping by first name is a heuristic,
  // the same one the designs' own "Mary" / "Mary - 2" disambiguation implies.
  const { journals: myJournals } = useMyAllChildJournals();

  const existingChildren = useMemo(
    () =>
      groupJournalsByChild(
        myJournals.filter(j => j.firstName && j.firstName !== PLACEHOLDER_NAME && j.id !== journalId && !giftCertificates.find(gift => gift.childJournalId === j.id))
      ).map(group => {
        const latest = group.journals[group.journals.length - 1];
        return {
          key: group.key,
          firstName: group.firstName,
          gender: latest.raw?.attributes?.gender,
          dateOfBirth: latest.raw?.attributes?.['date-of-birth'],
          relationship: latest.raw?.attributes?.['subject-relation'],
          id: latest.id,
          subjectId: latest?.subjectId
        };
      }),
    [myJournals, journalId]
  );

  // Prefill the recipient once the certificate arrives (the fetch resolves after
  // first render, and the field stays editable afterwards).
  useEffect(() => {
    if (certificate?.recipientName) setRecipientName(certificate.recipientName);
  }, [certificate?.recipientName]);

  // Redemption prefills the baby's first name from the journal that came with the
  // gift (the giver may already have named it), rather than starting blank or
  // showing the generic placeholder. It stays editable.
  const { journal: existingJournal } = useChildJournal(journalId, {
    enabled: isRedemption,
  });
  const prefilledBabyName = existingJournal?.firstName;

  useEffect(() => {
    if (prefilledBabyName && prefilledBabyName !== PLACEHOLDER_NAME) {
      setBabyName(prefilledBabyName);
    }
  }, [prefilledBabyName]);

  const backToDashboard = tab => replace(`/account/${tab || 'unassigned'}`);

  // Steps are history entries, so going back one step is just a history pop —
  // which also keeps the browser's own Back button consistent. The exception is
  // the entry step: there's nothing to pop within the flow (and on a deep link
  // there may be no history at all), so leave for the dashboard explicitly.
  const onBack = () => {
    if (step === entryStep) {
      // Redemption has no dashboard behind it — the gift flow owns that history.
      if (isRedemption) return goBack();
      return backToDashboard(isEditingCertificate ? 'gifts' : undefined);
    }
    return goBack();
  };

  const savePatch = async attributes => {
    setIsSubmitting(true);
    setError(null);
    try {
      await patchChildJournal({ id: journalId, ...attributes });
      revalidateDashboard();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Naming the journal is what moves it out of the Unassigned tab, so it happens
  // here rather than being deferred to the end of the flow.
  const onBabyContinue = async () => {
    const attributes = {};
    if (babyName.trim()) attributes['first-name'] = babyName.trim();
    if (gender) attributes.gender = gender;
    // Only a complete MM/DD/YYYY is a valid date — a partial selection is
    // dropped rather than sent, since the field is optional ("leave blank if
    // undecided yet").
    if (birthMonth && birthDay && birthYear) {
      const month = String(birthMonth).padStart(2, '0');
      const day = String(birthDay).padStart(2, '0');
      attributes['date-of-birth'] = `${birthYear}-${month}-${day}`;
    }
    if (Object.keys(attributes).length) {
      const ok = await savePatch(attributes);
      if (!ok) return;
    }
    // Redemption's journal already has the edition it was gifted with — asking
    // again here would let the member silently change what the giver picked.
    goToStep(isGcFlow ? STEP_RELATIONSHIP : STEP_EDITION);
  };

  // An existing child's details (name, gender, birthdate, relationship) are
  // reused as-is rather than asked again — only the edition is new. The
  // journal is assigned in this same patch, same as "For My Baby" ends with
  // today, since there's no separate relationship step left to do it in.
  const onExistingChildSelect = async child => {
    setExistingChild(child);

    const attributes = {
      'first-name': child.firstName, state: 'initialized',
      'subject-id': child.subjectId,
    };
    if (child.gender) attributes.gender = child.gender;
    if (child.dateOfBirth) attributes['date-of-birth'] = child.dateOfBirth;
    if (child.relationship) attributes['subject-relation'] = child.relationship;
    const ok = await savePatch(attributes);
    if (!ok) return;
    goToStep(STEP_EDITION);
  };

  const onAddNewChild = () => {
    setExistingChild(null);
    goToStep(STEP_BABY);
  };

  const onEditionContinue = async () => {
    const ok = await savePatch({ edition });
    if (!ok) return;
    // Reused an existing child's relationship above — nothing left to ask.
    if (existingChild) {
      backToDashboard('my-books');
      return;
    }
    goToStep(STEP_RELATIONSHIP);
  };

  const onRelationshipSelect = async value => {
    setRelationship(value);

    // "For My Baby" completes the assignment here, so the journal moves
    // unassigned → assigned in the same PATCH as the relationship. The gift path
    // doesn't: it becomes assigned when the certificate is submitted (see
    // gift-certificate/GiftCertificateDesign.js).
    const attributes = { 'subject-relation': value };
    if (!isGift) { attributes.state = 'assigned'; }
    if (window.location.search.includes('flow=redemption')) {
      attributes.state = 'initialized';
    }
    const ok = await savePatch(attributes);
    if (!ok) return;

    if (isGift) {
      // Hand off to the existing gift-certificate wizard for "Personalize your
      // gift certificate" / "Send certificate to my email" rather than
      // duplicating it.
      const query = new URLSearchParams({ step: '3' });
      if (journalId) query.set('childJournalId', journalId);
      if (recipientName.trim()) query.set('recipientName', recipientName.trim());
      // Editing: pass the id so the wizard PATCHes this certificate instead of
      // creating a second one.
      if (giftCertificateId) query.set('certificateId', giftCertificateId);
      push(`/gift?${query.toString()}`);
      return;
    }

    // Redemption ends here: straight into the journal that transferred with the
    // gift, rather than continuing into the dashboard onboarding steps.
    if (isRedemption) {
      replace(`/child-journals/${journalId}`);
      return;
    }

    // "After adding a book to 'My Books' we should land in this tab".
    backToDashboard('my-books');
  };

  const renderStep = () => {
    if (step === STEP_WHO) {
      return (
        <>
          <SetupHeading>Who is creating this book?</SetupHeading>
          <OptionList>
            <ChoiceCard
              type="button"
              selected={isGift === true}
              onClick={() => {
                goToStep(STEP_RECIPIENT, { for: 'gift' });
              }}
            >
              <ChoiceTitle>Someone else</ChoiceTitle>
              <ChoiceCopy>I&rsquo;m giving a gift certificate to a parent</ChoiceCopy>
            </ChoiceCard>
            <ChoiceCard
              type="button"
              selected={isGift === false && step !== STEP_WHO}
              onClick={() => {
                goToStep(existingChildren.length ? STEP_CHILD : STEP_BABY, { for: 'me' });
              }}
            >
              <ChoiceTitle>Me</ChoiceTitle>
              <ChoiceCopy>I am creating the book myself</ChoiceCopy>
            </ChoiceCard>
          </OptionList>
        </>
      );
    }

    if (step === STEP_CHILD) {
      return (
        <>
          <SetupHeading>Which child is this book about?</SetupHeading>
          <OptionList>
            {existingChildren.map(child => (
              <OptionPill
                key={child.key}
                type="button"
                disabled={isSubmitting}
                onClick={() => onExistingChildSelect(child)}
              >
                {child.firstName}
              </OptionPill>
            ))}
          </OptionList>
          <TextLink type="button" onClick={onAddNewChild}>
            + Add a new child
          </TextLink>
        </>
      );
    }

    if (step === STEP_RECIPIENT) {
      return (
        <>
          <SetupHeading>Who are you gifting this to?</SetupHeading>
          <SetupHint>
            Enter the parent or family&rsquo;s name — you&rsquo;ll personalize a gift certificate
            for them later.
          </SetupHint>
          <div>
            <FieldLabel htmlFor="recipient-name">Recipient</FieldLabel>
            <TextInput
              id="recipient-name"
              value={recipientName}
              placeholder="Enter gift receiver’s name"
              onChange={event => setRecipientName(event.target.value)}
            />
          </div>
        </>
      );
    }

    if (step === STEP_BABY) {
      return (
        <>
          <SetupHeading>About the child</SetupHeading>
          <div>
            <FieldLabel htmlFor="baby-name">Child&rsquo;s first name</FieldLabel>
            <TextInput
              id="baby-name"
              value={babyName}
              placeholder=""
              onChange={event => setBabyName(event.target.value)}
            />
            <SetupHint>Leave blank if undecided yet</SetupHint>
          </div>
          {!isGcFlow && (
            <div>
              <FieldLabel as="p" htmlFor="birth-month">Enter birthdate or due date</FieldLabel>
              <DateRow>
                <DateSelect
                  id="birth-month"
                  aria-label="Birth month"
                  value={birthMonth}
                  onChange={event => setBirthMonth(event.target.value)}
                >
                  <option value="">MM</option>
                  {MONTHS.map((month, index) => (
                    <option key={month} value={index + 1}>{month}</option>
                  ))}
                </DateSelect>
                <DateSelect
                  aria-label="Birth day"
                  value={birthDay}
                  onChange={event => setBirthDay(event.target.value)}
                >
                  <option value="">DD</option>
                  {DAYS.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </DateSelect>
                <DateSelect
                  aria-label="Birth year"
                  value={birthYear}
                  onChange={event => setBirthYear(event.target.value)}
                >
                  <option value="">YYYY</option>
                  {YEARS.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </DateSelect>
              </DateRow>
            </div>)}
          <div>
            <FieldLabel as="p">Child is a</FieldLabel>
            <OptionList>
              {GENDERS.map(option => (
                <OptionPill
                  key={option.value}
                  type="button"
                  selected={gender === option.value}
                  onClick={() => setGender(option.value)}
                >
                  {option.label}
                </OptionPill>
              ))}
            </OptionList>
          </div>
        </>
      );
    }

    if (step === STEP_EDITION) {
      return (
        <>
          <SetupHeading>Select the year this book will be about</SetupHeading>
          <OptionList>
            {EDITIONS.map(option => (
              <OptionPill
                key={option.value}
                type="button"
                selected={edition === option.value}
                aria-pressed={edition === option.value}
                onClick={() => setEdition(option.value)}
              >
                {option.label}
              </OptionPill>
            ))}
          </OptionList>
        </>
      );
    }

    return (
      <>
        <SetupHeading>Your relationship to the baby</SetupHeading>
        <OptionList>
          {RELATIONSHIPS.map(option => (
            <OptionPill
              key={option}
              type="button"
              selected={relationship === option}
              disabled={isSubmitting}
              onClick={() => onRelationshipSelect(option)}
            >
              {option}
            </OptionPill>
          ))}
        </OptionList>
      </>
    );
  };

  // Only the steps that don't advance on selection need an explicit continue
  // button. The edition step's button reads "Confirm" rather than "Continue",
  // per the design.
  const continueStep =
    (step === STEP_RECIPIENT && {
      label: 'Continue',
      onClick: () => goToStep(STEP_BABY),
      disabled: !recipientName.trim(),
    }) ||
    (step === STEP_BABY && { label: 'Continue', onClick: onBabyContinue, disabled: false }) ||
    (step === STEP_EDITION && { label: 'Confirm', onClick: onEditionContinue, disabled: false }) ||
    null;

  // Wait for the certificate before rendering the prefilled recipient step.
  if (isEditingCertificate && giftsLoading) {
    return (
      <Page>
        <DashboardTopNav onRedeemGift={() => push('/redeem-gift-certificate')} />
        <SetupContent>
          <Loading local />
        </SetupContent>
      </Page>
    );
  }

  if (isEditingCertificate && !certificate) {
    return (
      <Page>
        <DashboardTopNav onRedeemGift={() => push('/redeem-gift-certificate')} />
        <SetupContent>
          <ErrorText>We couldn&rsquo;t find that gift certificate.</ErrorText>
        </SetupContent>
        <ConfirmBar>
          <BackButton type="button" onClick={() => backToDashboard('gifts')}>
            <Chevron direction="left" />
            Back
          </BackButton>
        </ConfirmBar>
      </Page>
    );
  }

  return (
    <Page>
      <DashboardTopNav onRedeemGift={() => push('/redeem-gift-certificate')} />

      <SetupContent>
        {renderStep()}
        {!!error && (
          <ErrorText>
            {serverErrorMessage(error, "We couldn't save that. Please try again.")}
          </ErrorText>
        )}
      </SetupContent>

      <ConfirmBar>
        <BackButton type="button" onClick={onBack}>
          <Chevron direction="left" />
          Back
        </BackButton>
        {continueStep && (
          <PrimaryButton
            inline
            type="button"
            disabled={continueStep.disabled || isSubmitting}
            onClick={continueStep.onClick}
          >
            {isSubmitting ? 'Saving' : continueStep.label}
          </PrimaryButton>
        )}
      </ConfirmBar>
    </Page>
  );
};

export default AccountSetup;
